import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, BarChart3, Database, Code, Users, Award, Rocket, BookOpen, TrendingUp, User, Calendar, Clock, Shield, Lock, Star, Sparkles, Globe, Cpu, Lightbulb, Activity, PieChart, ChevronRight, Zap, MessageCircle, Video, Gift
} from 'lucide-react';

const testimonials = [
  {
    name: "Aarav Sharma",
    role: "Data Analyst",
    quote: "The OSOP Data Science Masterclass with Manish Sir was a game-changer. The hands-on projects and real-world insights made all the difference!",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  {
    name: "Priya Verma",
    role: "ML Engineer",
    quote: "I loved the focus on practical machine learning and data visualization. Manish Sir's teaching style is inspiring!",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg"
  }
];

const alumni = [
  {
    name: "Rohit Singh",
    preRole: "Support Engineer",
    postRole: "Data Scientist @ Flipkart",
    avatar: "https://randomuser.me/api/portraits/men/45.jpg"
  },
  {
    name: "Sneha Patel",
    preRole: "BSc Student",
    postRole: "Jr. Data Analyst @ TCS",
    avatar: "https://randomuser.me/api/portraits/women/65.jpg"
  }
];

const upcomingEvents = [
  {
    title: "AI in the Real World: Industry Applications",
    date: "August 10, 2024",
    time: "6:00 PM - 8:00 PM",
    instructor: "Manish Bajpai"
  }
];

const topics = [
  { icon: <Code className="w-6 h-6" />, title: "Python for Data Science" },
  { icon: <BarChart3 className="w-6 h-6" />, title: "Data Visualization (Matplotlib, Seaborn)" },
  { icon: <Database className="w-6 h-6" />, title: "SQL & Data Wrangling" },
  { icon: <Cpu className="w-6 h-6" />, title: "Machine Learning (scikit-learn)" },
  { icon: <Activity className="w-6 h-6" />, title: "Deep Learning (TensorFlow, PyTorch)" },
  { icon: <PieChart className="w-6 h-6" />, title: "Business Analytics" },
  { icon: <Lightbulb className="w-6 h-6" />, title: "AI & Real-World Projects" },
];

const MasterclassDataScience = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleRegister = (e) => {
    e.preventDefault();
    setIsFormSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0e1129] dark:from-[#0a0f1c] dark:via-[#1a1a2e] dark:to-[#0e1129] relative">
      {/* Hero Section - Advanced */}
      <section className="relative flex items-center justify-center min-h-[calc(100vh-96px)] pt-20 pb-8 px-4 mt-12 overflow-hidden">
        {/* Animated SVG Neural Network Background */}
        <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 1440 800" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <radialGradient id="bg1" cx="50%" cy="50%" r="80%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="#00ffe7" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#1e293b" stopOpacity="0.0" />
            </radialGradient>
            <linearGradient id="neon" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00ffe7" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
          </defs>
          <rect width="1440" height="800" fill="url(#bg1)" />
          {/* Neural network lines */}
          <g stroke="url(#neon)" strokeWidth="2" opacity="0.18">
            <polyline points="100,200 300,100 500,300 700,200 900,400 1100,250 1300,350" />
            <polyline points="200,600 400,500 600,700 800,600 1000,750 1200,650 1400,700" />
            <polyline points="300,400 600,200 900,500 1200,300" />
          </g>
          {/* Neural nodes */}
          <g>
            <circle cx="300" cy="100" r="8" fill="#00ffe7" opacity="0.5" />
            <circle cx="700" cy="200" r="10" fill="#a855f7" opacity="0.5" />
            <circle cx="900" cy="400" r="7" fill="#00ffe7" opacity="0.5" />
            <circle cx="1100" cy="250" r="9" fill="#a3e635" opacity="0.5" />
            <circle cx="600" cy="200" r="6" fill="#a3e635" opacity="0.5" />
            <circle cx="900" cy="500" r="8" fill="#00ffe7" opacity="0.5" />
          </g>
        </svg>
        <div className="w-full max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative z-10">
          {/* Left: Headline, Subheadline, USPs, Mentor */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-7">
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-4xl md:text-5xl xl:text-6xl font-extrabold bg-gradient-to-r from-cyan-400 via-purple-400 to-lime-300 bg-clip-text text-transparent drop-shadow-[0_2px_24px_rgba(0,255,231,0.25)]" style={{ lineHeight: 1.1 }}>
              Masterclass: Data Science & AI
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="text-xl md:text-2xl text-cyan-200 font-medium max-w-xl">
              <span className="text-lime-300 font-semibold">Unlock the future</span> with India's most advanced Data Science Masterclass, mentored by <span className="font-bold text-white">Manish Bajpai Sir</span>.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="flex flex-wrap justify-center md:justify-start gap-3">
              {[
                { icon: <Shield className="w-5 h-5 text-cyan-400" />, label: 'Industry Experts' },
                { icon: <Award className="w-5 h-5 text-purple-400" />, label: 'Real AI Projects' },
                { icon: <TrendingUp className="w-5 h-5 text-lime-300" />, label: 'Career Support' },
              ].map((usp, i) => (
                <span key={i} className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md border border-cyan-400/20 rounded-full text-sm font-semibold text-cyan-100 shadow-sm gap-2">
                  {usp.icon} {usp.label}
                </span>
              ))}
            </motion.div>
            {/* Mentor Card with Live Status */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }} className="flex items-center space-x-4 bg-white/10 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-cyan-400/30 mt-2 w-full max-w-xs mx-auto md:mx-0">
              <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-4 border-cyan-400 shadow-lg">
                <img src="https://osopcoding.com/assets/images/mentors/manish-bajpai.jpg" alt="Manish Bajpai Sir" className="w-full h-full object-cover" />
                {/* Live Status */}
                <span className="absolute -top-2 -right-2 flex items-center">
                  <span className="relative flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-lime-400 border-2 border-white"></span>
                  </span>
                  <span className="ml-1 text-xs text-lime-300 font-bold">Live</span>
                </span>
              </div>
              <div>
                <p className="font-bold text-white text-lg">Manish Bajpai</p>
                <p className="text-xs text-cyan-200">Lead Data Scientist & Mentor</p>
                <div className="flex items-center space-x-1 mt-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-xs text-cyan-100">4.9/5 (1,200+ reviews)</span>
                </div>
              </div>
            </motion.div>
          </div>
          {/* Right: Neon Glassmorphism Registration Card */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }} className="flex justify-center items-center w-full">
            <div className="relative bg-gradient-to-br from-[#0f172a]/80 via-[#1e293b]/80 to-[#0e1129]/80 rounded-3xl p-8 shadow-2xl border-2 border-cyan-400/40 backdrop-blur-xl neon-glow">
              <div className="absolute -inset-1 rounded-3xl pointer-events-none border-2 border-cyan-400/40 blur-[6px] animate-pulse" style={{ zIndex: 0 }}></div>
              <div className="text-center mb-8 relative z-10">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-2xl flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-cyan-100">OSOPCoding</h2>
                    <p className="text-sm text-cyan-300">Data Science Masterclass</p>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">Register for Free</h3>
                <p className="text-cyan-200">Join 2,000+ aspiring data scientists</p>
              </div>
              {!isFormSubmitted ? (
                <form onSubmit={handleRegister} className="space-y-6 relative z-10">
                  <div>
                    <label className="block text-sm font-semibold text-cyan-200 mb-3">
                      <User className="inline w-4 h-4 mr-2 text-cyan-400" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Enter your phone number"
                      className="w-full pl-4 pr-4 py-4 border-2 border-cyan-400/40 rounded-2xl focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 bg-[#181c2a] text-cyan-100 transition-all duration-200"
                      required
                    />
                  </div>
                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full bg-gradient-to-r from-cyan-400 to-purple-500 hover:from-cyan-500 hover:to-purple-600 text-white font-bold py-5 px-6 rounded-2xl transition-all duration-200 shadow-xl hover:shadow-2xl text-lg">
                    <div className="flex items-center justify-center space-x-2">
                      <Rocket className="w-5 h-5" />
                      <span>Register for Free Masterclass</span>
                    </div>
                  </motion.button>
                </form>
              ) : (
                <div className="text-center text-cyan-200 font-semibold text-lg py-8 relative z-10">
                  Thank you for registering! We'll contact you soon.
                </div>
              )}
              <div className="mt-8 space-y-4 relative z-10">
                <div className="bg-gradient-to-r from-cyan-400/10 to-purple-400/10 rounded-xl p-4 border border-cyan-400/20">
                  <div className="flex items-center justify-center space-x-2 text-cyan-300">
                    <Users className="w-5 h-5" />
                    <span className="font-semibold">2,000+ already registered</span>
                  </div>
                </div>
                <div className="flex items-center justify-center space-x-6 text-xs text-cyan-400">
                  <div className="flex items-center space-x-1">
                    <Shield className="w-3 h-3" />
                    <span>100% Free</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Zap className="w-3 h-3" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Star className="w-3 h-3" />
                    <span>2 Hours</span>
                  </div>
                </div>
                <p className="text-xs text-cyan-400 text-center">
                  By registering, you agree to our{' '}
                  <span className="text-cyan-300 hover:underline cursor-pointer">terms and conditions</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
        {/* Floating WhatsApp Button */}
        <a
          href="https://wa.me/917649098000?text=Hi!%20I%20am%20interested%20in%20the%20Data%20Science%20Masterclass."
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-8 right-8 z-50 bg-gradient-to-br from-cyan-400 to-green-400 p-4 rounded-full shadow-2xl hover:scale-110 transition-transform duration-200 flex items-center group"
          aria-label="Chat on WhatsApp"
        >
          <MessageCircle className="w-7 h-7 text-white group-hover:animate-bounce" />
        </a>
      </section>
      {/* About Section */}
      <section className="relative py-20 bg-transparent">
        {/* Animated SVG background */}
        <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 1440 400" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <radialGradient id="aboutBg" cx="50%" cy="50%" r="80%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#0e1129" stopOpacity="0.0" />
            </radialGradient>
          </defs>
          <ellipse cx="720" cy="200" rx="700" ry="180" fill="url(#aboutBg)" />
        </svg>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyan-400/20 to-purple-400/20 text-cyan-200 rounded-full text-sm font-semibold mb-6 shadow-lg border border-cyan-400/30">
                <Zap className="w-4 h-4 mr-2 animate-pulse text-lime-300" />
                OSOP Data Science Masterclass
              </motion.div>
              <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-lime-300 mb-6 animate-gradient-text">
                About <span className="text-lime-300">Data Science</span>
              </motion.h2>
              <p className="text-xl text-cyan-100 leading-relaxed max-w-3xl mx-auto">
                Data Science is the art and science of extracting insights from data using programming, statistics, and machine learning. It powers everything from business analytics to artificial intelligence. Join this masterclass to gain practical skills and a deep understanding of the data-driven world.
              </p>
            </div>
            <div className="bg-gradient-to-r from-cyan-400/10 via-purple-400/10 to-lime-300/10 rounded-3xl p-8 border-2 border-cyan-400/20 shadow-2xl mb-16 glassy-panel">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex items-center justify-center animate-pulse">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-cyan-100">
                    Ready to launch your Data Science career?
                  </h3>
                </div>
                <p className="text-cyan-200 mb-6 max-w-2xl mx-auto">
                  Learn from the best, work on real projects, and get certified by OSOPCoding.
                </p>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="bg-gradient-to-r from-cyan-400 to-purple-500 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-200">
                  <div className="flex items-center space-x-2">
                    <Rocket className="w-5 h-5" />
                    <span>Register Now - It's Free!</span>
                  </div>
                </motion.button>
              </div>
            </div>
            {/* What You'll Learn */}
            <div className="mt-16">
              <div className="text-center mb-12">
                <motion.h3 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-lime-300 mb-4 animate-gradient-text">
                  What You'll Learn in this <span className="text-lime-300">Data Science Masterclass</span>
                </motion.h3>
                <p className="text-lg text-cyan-100 max-w-2xl mx-auto">
                  Get hands-on with the most in-demand data science skills and tools.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {topics.map((item, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-lg border border-cyan-400/30 hover:shadow-xl transition-all duration-200 group glassy-panel">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-purple-400/30 rounded-xl flex items-center justify-center text-cyan-300 group-hover:scale-110 transition-transform">
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-cyan-100 mb-2">
                          {item.title}
                        </h4>
                        {/* Animated progress bar */}
                        <div className="w-full h-2 bg-cyan-900/30 rounded-full overflow-hidden mt-2">
                          <motion.div initial={{ width: 0 }} whileInView={{ width: `${80 + index * 3}%` }} transition={{ duration: 1.2, delay: 0.2 + index * 0.1 }} className="h-2 rounded-full bg-gradient-to-r from-cyan-400 via-purple-400 to-lime-300 shadow-lg"></motion.div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mentor Section */}
      <section className="py-20 bg-transparent relative">
        {/* Animated SVG background */}
        <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <radialGradient id="mentorBg1" cx="30%" cy="30%" r="60%" fx="30%" fy="30%">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#0e1129" stopOpacity="0.0" />
            </radialGradient>
            <radialGradient id="mentorBg2" cx="70%" cy="70%" r="60%" fx="70%" fy="70%">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#0e1129" stopOpacity="0.0" />
            </radialGradient>
          </defs>
          <ellipse cx="432" cy="180" rx="400" ry="120" fill="url(#mentorBg1)" />
          <ellipse cx="1008" cy="420" rx="400" ry="120" fill="url(#mentorBg2)" />
        </svg>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-6xl mx-auto">
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-lime-300 text-center mb-16 animate-gradient-text">
              Meet Our <span className="text-lime-300">Expert Mentors</span>
            </motion.h2>
            
            {/* Mentors Grid */}
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Dr. Dharmendra Bajpai Sir */}
              <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }} className="relative group">
                <div className="bg-gradient-to-br from-purple-400/20 via-cyan-400/20 to-lime-300/20 rounded-3xl p-8 border-2 border-purple-400/30 shadow-2xl backdrop-blur-md glassy-panel hover:border-purple-400/50 transition-all duration-300">
                  {/* Mentor Image */}
                  <div className="relative mb-8">
                    <div className="w-72 h-72 mx-auto rounded-full overflow-hidden shadow-2xl border-4 border-purple-400/60 group-hover:border-purple-400/80 transition-all duration-300">
                      <img src="https://osopcoding.com/assets/images/mentors/dharmendra-bajpai.jpg" alt="Dr. Dharmendra Bajpai Sir" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="absolute -top-4 -right-4 bg-gradient-to-br from-purple-400 to-cyan-400 text-white rounded-full p-4 shadow-lg border-2 border-purple-400/40 animate-pulse">
                      <Award className="w-8 h-8" />
                    </div>
                    <div className="absolute -bottom-4 -left-4 bg-gradient-to-br from-lime-300 to-cyan-400 text-cyan-900 rounded-full px-4 py-2 shadow-lg border-2 border-lime-300/40 font-bold text-sm">
                      PhD
                    </div>
                  </div>
                  
                  {/* Mentor Info */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400 mb-2">
                      Dr. Dharmendra Bajpai Sir
                    </h3>
                    <p className="text-cyan-200 text-lg mb-4">Director & Chief Mentor, OSOPCoding</p>
                  </div>
                  
                  {/* Credentials */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">PhD in Computer Science</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">Ex-Senior Software Engineer, Microsoft</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">15+ Years in Software Development</span>
                      <span className="ml-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-400 to-cyan-400 text-cyan-900 font-bold text-xs shadow-lg border border-purple-400/40 animate-pulse">15+ Years</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">Mentored 10,000+ students</span>
                    </div>
                  </div>
                  
                  {/* Specialization */}
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-purple-400/30 shadow-lg">
                    <p className="text-cyan-100 text-center">
                      <strong>Specialization:</strong> Software Engineering, System Design, Career Guidance
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Manish Bajpai Sir */}
              <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.4 }} viewport={{ once: true }} className="relative group">
                <div className="bg-gradient-to-br from-cyan-400/20 via-lime-300/20 to-purple-400/20 rounded-3xl p-8 border-2 border-cyan-400/30 shadow-2xl backdrop-blur-md glassy-panel hover:border-cyan-400/50 transition-all duration-300">
                  {/* Mentor Image */}
                  <div className="relative mb-8">
                    <div className="w-72 h-72 mx-auto rounded-full overflow-hidden shadow-2xl border-4 border-cyan-400/60 group-hover:border-cyan-400/80 transition-all duration-300">
                      <img src="https://osopcoding.com/assets/images/mentors/manish-bajpai.jpg" alt="Manish Bajpai Sir" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <div className="absolute -top-4 -right-4 bg-gradient-to-br from-cyan-400 to-lime-300 text-white rounded-full p-4 shadow-lg border-2 border-cyan-400/40 animate-pulse">
                      <Award className="w-8 h-8" />
                    </div>
                    <div className="absolute -bottom-4 -left-4 bg-gradient-to-br from-purple-400 to-cyan-400 text-cyan-900 rounded-full px-4 py-2 shadow-lg border-2 border-purple-400/40 font-bold text-sm">
                      Lead DS
                    </div>
                  </div>
                  
                  {/* Mentor Info */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-lime-300 mb-2">
                      Manish Bajpai Sir
                    </h3>
                    <p className="text-cyan-200 text-lg mb-4">Lead Data Scientist, OSOPCoding</p>
                  </div>
                  
                  {/* Credentials */}
                  <div className="space-y-4 mb-6">
                                         <div className="flex items-center space-x-3">
                       <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                       <span className="text-cyan-100">Lead Data Scientist, OSOPCoding</span>
                     </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">Ex-Data Scientist, IBM</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">10+ Years in Data Science & AI</span>
                      <span className="ml-2 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-400 to-lime-300 text-cyan-900 font-bold text-xs shadow-lg border border-cyan-400/40 animate-pulse">10+ Years</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                      <span className="text-cyan-100">Mentored 5,000+ students</span>
                    </div>
                  </div>
                  
                  {/* Specialization */}
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-cyan-400/30 shadow-lg">
                    <p className="text-cyan-100 text-center">
                      <strong>Specialization:</strong> Data Science, Machine Learning, AI, Analytics
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Certificate Info */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }} viewport={{ once: true }} className="mt-16">
              <div className="bg-gradient-to-r from-cyan-400/10 via-purple-400/10 to-lime-300/10 rounded-3xl p-8 border-2 border-cyan-400/20 shadow-2xl glassy-panel">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex items-center justify-center animate-pulse">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-cyan-100">
                      Official Certification
                    </h3>
                  </div>
                                     <p className="text-cyan-200 mb-4 max-w-2xl mx-auto">
                     <strong>All attendees get certificates from both Dr. Dharmendra Bajpai Sir and Manish Bajpai Sir,</strong> recognized by OSOPCoding and industry partners.
                   </p>
                  <div className="flex items-center justify-center space-x-4 text-sm text-cyan-300">
                    <span className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                      <span>Industry Recognized</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span>Lifetime Access</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-lime-300 rounded-full"></div>
                      <span>Verifiable Online</span>
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-gradient-to-r from-green-400 to-teal-500 rounded-full blur-lg opacity-20 animate-bounce"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent mb-6">
              What our Learners Say
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Testimonial 1 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:scale-105">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    AS
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg">Aarav Sharma</h4>
                    <p className="text-cyan-300 text-sm">Data Analyst @ Amazon</p>
                  </div>
                </div>
                
                <blockquote className="text-gray-300 mb-6 italic">
                  "The OSOPCoding Data Science Masterclass with Manish Sir was a game-changer. The hands-on projects and real-world insights made all the difference! I went from knowing nothing about ML to landing a role at Amazon."
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded-full border border-cyan-500/30">Python</span>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-500/30">ML</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-yellow-400">★</span>
                    <span className="text-white font-semibold">5.0</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:scale-105">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    PV
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg">Priya Verma</h4>
                    <p className="text-purple-300 text-sm">ML Engineer @ Google</p>
                  </div>
                </div>
                
                <blockquote className="text-gray-300 mb-6 italic">
                  "I loved the focus on practical machine learning and data visualization. Manish Sir's teaching style is inspiring! The projects were industry-relevant and helped me crack Google interviews."
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30">Deep Learning</span>
                    <span className="px-3 py-1 bg-pink-500/20 text-pink-300 text-xs rounded-full border border-pink-500/30">NLP</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-yellow-400">★</span>
                    <span className="text-white font-semibold">5.0</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alumni Success Stories Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-10 w-24 h-24 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-28 h-28 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full blur-xl opacity-20 animate-pulse delay-500"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-emerald-100 to-white bg-clip-text text-transparent mb-6">
              Alumni Success Stories
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-400 to-teal-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Success Story 1 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:scale-105">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    RS
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg">Rohit Singh</h4>
                    <p className="text-emerald-300 text-sm">From: Support Engineer</p>
                    <p className="text-teal-300 text-sm">To: Data Scientist @ Flipkart</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400 mb-1">+180%</div>
                    <div className="text-xs text-gray-400">Salary Increase</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-400 mb-1">4</div>
                    <div className="text-xs text-gray-400">Months</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400 mb-1">₹12L</div>
                    <div className="text-xs text-gray-400">Package</div>
                  </div>
                </div>
                
                <blockquote className="text-gray-300 italic">
                  "The Data Science Masterclass transformed my career completely. From a support role to a data scientist at Flipkart - dreams do come true!"
                </blockquote>
              </div>
            </div>

            {/* Success Story 2 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:scale-105">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    SP
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg">Sneha Patel</h4>
                    <p className="text-green-300 text-sm">From: BSc Student</p>
                    <p className="text-emerald-300 text-sm">To: Jr. Data Analyst @ TCS</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400 mb-1">3</div>
                    <div className="text-xs text-gray-400">Months</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400 mb-1">₹6.5L</div>
                    <div className="text-xs text-gray-400">Package</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-400 mb-1">100%</div>
                    <div className="text-xs text-gray-400">Success Rate</div>
                  </div>
                </div>
                
                <blockquote className="text-gray-300 italic">
                  "As a fresh graduate, I was worried about my career. The masterclass gave me the skills and confidence to land my dream job!"
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-orange-900/20 to-slate-900">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-orange-400 to-red-500 rounded-full blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full blur-xl opacity-20 animate-pulse delay-1000"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-orange-100 to-white bg-clip-text text-transparent mb-6">
                Upcoming Events
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full"></div>
            </div>
            <button className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105">
              Explore All
            </button>
          </div>

          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    AI in the Real World: Industry Applications
                  </h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-orange-400" />
                      <span className="text-gray-300">August 10, 2024</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Video className="w-5 h-5 text-red-400" />
                      <span className="text-gray-300">Live Interactive Session</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-yellow-400" />
                      <span className="text-gray-300">6:00 PM - 8:00 PM (2 Hours)</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">By Manish Bajpai</h4>
                      <p className="text-orange-300 text-sm">Lead Data Scientist</p>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-green-400 mb-2">FREE</div>
                    <div className="text-sm text-gray-400">Limited Seats Available</div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-gray-400 mb-2">
                      <span>Seats Filled</span>
                      <span>85%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="bg-gradient-to-r from-orange-400 to-red-500 h-2 rounded-full" style={{width: '85%'}}></div>
                    </div>
                  </div>
                  
                  <button className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105">
                    Register Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-900/20 to-slate-900">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-24 h-24 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full blur-xl opacity-20 animate-pulse delay-500"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-lg opacity-20 animate-bounce"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 hover:bg-white/10 transition-all duration-500">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-indigo-100 to-white bg-clip-text text-transparent mb-6">
                Ready to Master Data Science?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Join the Data Science Masterclass and transform your career with OSOPCoding.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Free Masterclass</h3>
                  <p className="text-gray-400 text-sm">No cost to attend</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Video className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Live Interactive</h3>
                  <p className="text-gray-400 text-sm">Real-time learning</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Certificate</h3>
                  <p className="text-gray-400 text-sm">Industry recognized</p>
                </div>
              </div>
              
              <button className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full font-semibold text-lg hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 mb-4">
                Register for Free Masterclass
              </button>
              <p className="text-gray-400 text-sm">
                Limited seats available • Join 10,000+ successful learners
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MasterclassDataScience; 